import React, { useContext, createContext, useMemo } from 'react';
import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { useContentId } from './useContentId.tsx';
import { ALLOWED_CONTENT_STATUSES } from './constants.ts';
import type {
	PageRestrictionsContextQuery$data as PageRestrictionsContextQueryDataType,
	PageRestrictionsContextQuery$variables,
} from './__generated__/PageRestrictionsContextQuery.graphql.ts';

type AncestorType = {
	id: string | null;
	title: string | null;
	hasRestrictions: boolean;
	links: { webui: string | null } | null;
};

export type RestrictionsType = {
	hasInheritedRestrictions: boolean;
	hasViewRestrictions: boolean;
	hasRestrictions: boolean;
	ancestors: AncestorType[];
};

export type PageRestrictionsContextValue = {
	loading: boolean;
	error?: ApolloErrorInterop;
	restrictions?: RestrictionsType;
	refetch: () => void;
};

export type PageRestrictionsContextProviderProps = {
	contentId?: string | symbol;
	children?: React.ReactNode;
	/** Eligible View Page routes have no eager consumer after the classic share cleanup. */
	deferQuery?: boolean;
};

const defaultContext: PageRestrictionsContextValue = {
	loading: false,
	refetch: () => {},
};

export const PageRestrictionsContext: React.Context<PageRestrictionsContextValue> =
	createContext<PageRestrictionsContextValue>(defaultContext);

// Kept separate from the public value so existing consumers keep the same data/refetch contract.
const DeferredRestrictionsContext = createContext<{ contentId?: string | symbol } | null>(null);

export function PageRestrictionsContextProvider(
	props: PageRestrictionsContextProviderProps,
): React.JSX.Element {
	const contentId = useContentId(props.contentId);
	const contextValue = useRestrictionsQuery(contentId, Boolean(props.deferQuery));
	const deferred = useMemo(
		() => (props.deferQuery ? { contentId } : null),
		[props.deferQuery, contentId],
	);

	// Keep both providers mounted across view/edit transitions; never move Screen's children.
	return (
		<DeferredRestrictionsContext.Provider value={deferred}>
			<PageRestrictionsContext.Provider value={contextValue}>
				{props.children}
			</PageRestrictionsContext.Provider>
		</DeferredRestrictionsContext.Provider>
	);
}

function useRestrictionsQuery(
	contentId: string | symbol | undefined,
	skip: boolean,
): PageRestrictionsContextValue {
	const resolvedContentId = typeof contentId === 'symbol' ? undefined : contentId?.toString();
	const { edition } = useSessionData() || {};
	const includeAncestors = ConfluenceEdition.FREE === edition;

	const { data, loading, error, refetch } = useQueryInterop<
		PageRestrictionsContextQueryDataType,
		PageRestrictionsContextQuery$variables
	>(
		graphql`
			query PageRestrictionsContextQuery(
				$contentId: ID!
				$status: [String]
				$includeAncestors: Boolean!
			) {
				singleContent(id: $contentId, status: $status) {
					id
					hasInheritedRestrictions
					hasViewRestrictions
					hasRestrictions
					ancestors @include(if: $includeAncestors) {
						id
						title
						hasRestrictions
						links {
							webui
						}
					}
				}
			}
		`,
		{
			variables: {
				contentId: resolvedContentId ?? '',
				status: ALLOWED_CONTENT_STATUSES,
				includeAncestors,
			},
			skip: skip || !resolvedContentId,
		},
	);

	if (!skip && error) {
		if (!isUnauthorizedError(error)) {
			getMonitoringClient().submitError(error, { attribution: 'backbone' });
		}
		markErrorAsHandled(error);
	}

	const contextValue = useMemo(
		() =>
			contentId && !skip
				? {
						loading,
						error,
						restrictions: transformData(data),
						refetch,
					}
				: defaultContext,
		[loading, error, data, refetch, contentId, skip],
	);

	return contextValue;
}

const transformData = (
	data?: PageRestrictionsContextQueryDataType,
): RestrictionsType | undefined => {
	const content = data?.singleContent;
	if (content) {
		return {
			hasRestrictions: content.hasRestrictions,
			hasViewRestrictions: content.hasViewRestrictions,
			hasInheritedRestrictions: content.hasInheritedRestrictions,
			ancestors:
				content.ancestors?.map<AncestorType>((a) => ({
					id: a?.id || null,
					title: a?.title || null,
					hasRestrictions: Boolean(a?.hasRestrictions),
					links: { webui: a?.links?.webui || null },
				})) || [],
		};
	}
};

export const usePageRestrictionsContext = ({
	skipWhenDeferred = false,
}: { skipWhenDeferred?: boolean } = {}): PageRestrictionsContextValue => {
	const context = useContext(PageRestrictionsContext);
	const deferred = useContext(DeferredRestrictionsContext);
	// Reuse the existing operation only when a real consumer needs it. Control routes still
	// use their shared eager provider; no child query runs there.
	const onDemand = useRestrictionsQuery(deferred?.contentId, !deferred || skipWhenDeferred);
	return deferred ? onDemand : context;
};

export function PageRestrictionsContextConsumer({
	children,
}: React.ConsumerProps<PageRestrictionsContextValue>): React.ReactNode {
	return children(usePageRestrictionsContext());
}
